import { Countdown } from '@/components/ui/Countdown';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useTranslation } from '@/hooks/useTranslation';
import { Separator } from '@ophelos/ui/components/separator';

interface PlanSummaryHeroProps {
  instalment: number;
  currency: string;
  paymentCount: number;
  frequencyLabel: string;
  total: number;
  /** Pre-discount figures; shown struck through beside the live ones when present. */
  originalInstalment?: number;
  originalTotal?: number;
  expiresAt?: string | null;
}

export function PlanSummaryHero({
  instalment,
  currency,
  paymentCount,
  frequencyLabel,
  total,
  originalInstalment,
  originalTotal,
  expiresAt,
}: PlanSummaryHeroProps) {
  const { t } = useTranslation();
  return (
    <div className="text-center space-y-4 w-full py-5 rounded-2xl border bg-soft border-border-heavy">
      <div className="px-5 stack gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-accent-foreground/75">{t('paymentPlan.youllPay')}</p>
        {originalInstalment !== undefined && (
          <div className="flex flex-col items-center gap-1">
            <CurrencyDisplay amount={originalInstalment} currency={currency} className="text-sm line-through text-destructive" />
            <Countdown to={expiresAt} />
          </div>
        )}
        <div className="text-4xl font-bold text-accent-foreground">
          <CurrencyDisplay amount={instalment} currency={currency} className="text-4xl" centsClassName="text-2xl" />
        </div>
      </div>
      <Separator className="my-4 bg-border" />
      <div className="px-5 flex items-start justify-between text-sm">
        <span className="text-foreground font-medium">
          {paymentCount} {t('paymentPlan.payments')} {frequencyLabel}
        </span>
        <span className="text-foreground font-medium flex gap-2">
          {t('paymentPlan.total')}
          <span className="stack items-end">
            <CurrencyDisplay amount={total} currency={currency} className="text-sm" />
            {originalTotal !== undefined && (
              <CurrencyDisplay amount={originalTotal} currency={currency} className="text-sm line-through text-destructive" />
            )}
          </span>
        </span>
      </div>
    </div>
  );
}
